import React, { useState, useEffect } from "react";
import Layout from "../../components/Layout";
import api from "../../api";
import { queuedMessage } from "../../offlineQueue";

// Team Stableford (Best 2 of 4): each fourball group has its own full
// scorecard -- every member enters their OWN gross per hole, off their own
// Course Handicap at 85% (R&A Appendix C's Best 2 of 4 figure -- fixed
// 2026-10-25, was running on full 100% allowance), and each hole's group
// value is the best 2 of whichever members have a score that hole (matches
// GroupBestTwoResults.jsx's own fallback computation exactly, and the
// kiosk's team-strokes screen -- all three must never drift apart).
export default function GroupBestTwoScoreEntry() {
  const [event, setEvent] = useState(null);
  const [holes, setHoles] = useState([]);
  const [groups, setGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  // { [player_id]: { [hole]: grossString } }
  const [holeGross, setHoleGross] = useState({});
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState("");

  useEffect(() => { loadAll(); }, []);
  useEffect(() => { if (selectedGroup) loadGroupScores(selectedGroup); }, [selectedGroup?.label]);

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(""), 3000); };
  const isMedal = event?.game_type?.includes("Medal") || event?.game_type?.includes("Stroke");

  const loadAll = async () => {
    try {
      const eventRes = await api.get("/events/current");
      if (!eventRes.data) { setLoading(false); return; }
      setEvent(eventRes.data);

      if (eventRes.data.course_id) {
        const holesRes = await api.get("/courses/" + eventRes.data.course_id + "/holes");
        setHoles(holesRes.data);
      }

      const groupRes = await api.get("/groups");
      const players = (groupRes.data.players || []).filter(p => p.playing);

      const grpMap = {};
      players.forEach(p => { if (!grpMap[p.grp]) grpMap[p.grp] = []; grpMap[p.grp].push(p); });

      const groupsArr = Object.entries(grpMap)
        .sort((a, b) => parseInt(a[0]) - parseInt(b[0]))
        .map(([grp, members]) => ({
          grp: parseInt(grp),
          members,
          label: `Group ${grp}: ` + members.map(m => m.name.split(" ")[0]).join(", "),
          tooFew: members.length < 2,
        }));

      setGroups(groupsArr);
      if (groupsArr.length > 0) setSelectedGroup(groupsArr[0]);
      else setLoading(false);
    } catch(e) { console.error(e); setLoading(false); }
  };

  const loadGroupScores = async (group) => {
    if (!event) return;
    setLoading(true);
    try {
      const scoresRes = await api.get("/scores/" + event.id);
      const gross = {};
      group.members.forEach(m => {
        gross[m.player_id] = {};
        holes.forEach(h => {
          const s = scoresRes.data.find(sc => sc.player_id === m.player_id && sc.hole === h.hole && sc.gross);
          if (s) gross[m.player_id][h.hole] = s.gross;
        });
      });
      setHoleGross(gross);
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  };

  // 85% of Course Handicap per R&A Rules of Handicapping Appendix C
  // (Best 2 of 4 stroke play) -- fixed 2026-10-25 after the AI Scoring
  // Verification Wizard confirmed this format had been running on full
  // (100%) handicap allowance instead, a genuine scoring bug.
  const getShots = (member, hole) => {
    const hcp = Math.round(((member.hcp_adj ?? member.handicap) || 0) * 0.85);
    const base = Math.floor(hcp / 18);
    const extra = hcp % 18;
    return base + (hole.stroke_index <= extra ? 1 : 0);
  };

  const getMemberValue = (member, hole) => {
    const gross = parseInt(holeGross[member.player_id]?.[hole.hole]);
    if (!gross) return null;
    const net = gross - getShots(member, hole);
    return isMedal ? net : Math.max(0, 2 + (hole.par - net));
  };

  const getHoleTotal = (hole) => {
    if (!selectedGroup) return null;
    const values = selectedGroup.members.map(m => getMemberValue(m, hole)).filter(v => v !== null);
    if (values.length === 0) return null;
    values.sort((a, b) => isMedal ? a - b : b - a);
    const best2 = values.slice(0, 2);
    return best2.reduce((sum, v) => sum + v, 0);
  };

  const getRunningTotal = (upToIndex) => {
    let total = 0, any = false;
    for (let i = 0; i <= upToIndex; i++) {
      const h = holes[i];
      if (!h) continue;
      const v = getHoleTotal(h);
      if (v !== null) { total += v; any = true; }
    }
    return any ? total : "";
  };

  const groupTotal = holes.length ? getRunningTotal(holes.length - 1) : "";

  const handleScoreChange = (playerId, hole, val) => {
    setHoleGross(prev => ({ ...prev, [playerId]: { ...prev[playerId], [hole]: val } }));
  };

  const buildGroupSaveScores = () => {
    const allScores = [];
    if (!selectedGroup) return allScores;
    selectedGroup.members.forEach(m => {
      holes.forEach(h => {
        const gross = parseInt(holeGross[m.player_id]?.[h.hole]);
        if (gross) allScores.push({ player_id: m.player_id, hole: h.hole, gross });
      });
    });
    return allScores;
  };

  const saveScores = async () => {
    if (!event || !selectedGroup) return;
    try {
      const res = await api.post("/scores/save", { event_id: event.id, scores: buildGroupSaveScores(), publish: false });
      showToast(queuedMessage(res, "Scores saved!"));
    } catch(e) { showToast("Failed: " + e.message); }
  };

  const randomScores = () => {
    if (!selectedGroup) return;
    const gross = {};
    selectedGroup.members.forEach(m => {
      gross[m.player_id] = {};
      holes.forEach(h => { gross[m.player_id][h.hole] = h.par + Math.floor(Math.random() * 3) - 1; });
    });
    setHoleGross(gross);
    showToast("Random scores generated");
  };

  const randomAllGroups = async () => {
    if (!event || groups.length === 0) return;
    try {
      const allScores = [];
      groups.forEach(group => {
        group.members.forEach(m => {
          holes.forEach(h => {
            allScores.push({ player_id: m.player_id, hole: h.hole, gross: h.par + Math.floor(Math.random() * 3) - 1 });
          });
        });
      });
      const res = await api.post("/scores/save", { event_id: event.id, scores: allScores, publish: false });
      showToast(queuedMessage(res, "Random scores generated for all " + groups.length + " groups"));
      if (selectedGroup) loadGroupScores(selectedGroup);
    } catch(e) { showToast("Failed: " + e.message); }
  };

  return (
    <Layout>
      {toast && <div style={{position:"fixed",bottom:"20px",right:"20px",zIndex:9999}} className="bg-green-600 text-white px-4 py-3 rounded shadow-lg text-sm font-medium">{toast}</div>}
      <div className="flex flex-col h-full">
        <div className="bg-white border-b border-gray-300 px-4 py-2 flex items-center gap-4">
          <span className="font-semibold text-sm">{event?.game_type || "Team Stableford (Best 2 of 4)"} Score Entry</span>
          <div className="ml-auto flex gap-2">
            <button type="button" onClick={saveScores} className="bg-green-600 hover:bg-green-700 text-white text-xs font-semibold px-3 py-1.5 rounded">Save Scores</button>
            <button type="button" onClick={randomScores} className="bg-purple-600 hover:bg-purple-700 text-white text-xs font-semibold px-3 py-1.5 rounded">Random Scores</button>
            <button type="button" onClick={randomAllGroups} className="bg-pink-600 hover:bg-pink-700 text-white text-xs font-semibold px-3 py-1.5 rounded">Random ALL Groups</button>
          </div>
        </div>

        <div className="flex flex-1 overflow-hidden">
          <div className="w-56 flex-shrink-0 border-r border-gray-300 bg-yellow-50 overflow-y-auto">
            <div className="px-3 py-2 text-sm font-extrabold text-green-900 uppercase tracking-wide sticky top-0 bg-yellow-200 border-b-2 border-green-800">Every Group Playing</div>
            <div className="flex flex-col">
              {groups.map((g, i) => (
                <button key={g.label} type="button" onClick={() => setSelectedGroup(g)}
                  className={"text-left text-xs font-semibold px-3 py-2 border-b border-yellow-100 " +
                    (selectedGroup?.label === g.label
                      ? "bg-blue-600 text-white"
                      : (i % 2 === 0 ? "bg-white" : "bg-yellow-100") + " text-gray-700 hover:bg-blue-50")}>
                  {g.tooFew && "⚠️ "}{g.label}
                </button>
              ))}
              {groups.length === 0 && <div className="px-3 py-4 text-xs text-gray-500">No groups found.</div>}
            </div>
          </div>

          <div className="flex-1 flex flex-col overflow-hidden">
            <div className="bg-white border-b border-gray-300 px-4 py-2 flex items-center gap-4 flex-wrap">
              {selectedGroup && (
                <span className="text-xs text-gray-600">
                  {selectedGroup.members.map(m => `${m.name.split(" ")[0]} (HCP ${m.hcp_adj ?? m.handicap})`).join(" · ")}
                </span>
              )}
              <div className="ml-auto bg-green-800 text-white rounded-lg px-5 py-2 text-center">
                <div className="text-[10px] uppercase tracking-wide text-green-200">{isMedal ? "Best 2 Total Net" : "Best 2 Total Points"}</div>
                <div className="text-2xl font-extrabold leading-none">{groupTotal}</div>
              </div>
            </div>

            {selectedGroup?.tooFew && (
              <div className="bg-yellow-100 border-b border-yellow-300 px-4 py-2 text-xs text-yellow-800 font-medium">
                ⚠️ Fewer than 2 players in this group — a best-2-of-group total needs at least 2 scores entered each hole.
              </div>
            )}

            {loading && <div className="bg-blue-50 text-blue-700 px-4 py-2 text-sm">Loading...</div>}

            <div className="flex-1 overflow-auto">
              <table className="w-full text-sm border-collapse">
                <thead className="sticky top-0 bg-green-800 text-white">
                  <tr>
                    <th className="px-3 py-2 text-left w-12">Hole</th>
                    <th className="px-3 py-2 text-center w-12">Par</th>
                    <th className="px-3 py-2 text-center w-12">SI</th>
                    {selectedGroup?.members.map(m => (
                      <th key={m.player_id} className="px-3 py-2 text-center w-24">{m.name.split(" ")[0]}</th>
                    ))}
                    <th className="px-3 py-2 text-center w-20">Best 2</th>
                    <th className="px-3 py-2 text-center w-20">Running</th>
                  </tr>
                </thead>
                <tbody>
                  {holes.map((hole, i) => {
                    const holeTotal = getHoleTotal(hole);
                    const running = getRunningTotal(i);
                    return (
                      <tr key={hole.hole} className={"border-b border-gray-200 " + (i % 2 === 0 ? "bg-white" : "bg-green-50")}>
                        <td className="px-3 py-1.5 font-medium">{hole.hole}</td>
                        <td className="px-3 py-1.5 text-center">{hole.par}</td>
                        <td className="px-3 py-1.5 text-center text-gray-500">{hole.stroke_index}</td>
                        {selectedGroup?.members.map(m => (
                          <td key={m.player_id} className="px-3 py-1.5 text-center">
                            <input type="number" min="1" max="15"
                              value={holeGross[m.player_id]?.[hole.hole] || ""}
                              onChange={e => handleScoreChange(m.player_id, hole.hole, e.target.value)}
                              className="border border-gray-300 rounded px-1 py-0.5 w-14 text-xs text-center focus:border-blue-500 focus:outline-none"
                            />
                          </td>
                        ))}
                        <td className="px-3 py-1.5 text-center font-bold text-blue-700">{holeTotal === null ? "" : holeTotal}</td>
                        <td className="px-3 py-1.5 text-center font-bold">{running}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {!loading && holes.length === 0 && (
                <div className="text-center py-8 text-gray-500">No course loaded. Go to Course page and select a course.</div>
              )}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
